import { Flex, Text } from '@chakra-ui/react';
import { Tag } from '@whalar/ui/src/components/ui/tag';
import { Flame, Megaphone } from 'lucide-react';
import { Chip } from '~/components/Chip';
import { CloseIconNew, FireIcon, ReachIcon } from '~/components/icons';
import {
  getFilterName,
  isEngagementRate,
} from '~/screens/MyAgency/components/AppliedFilters/utils';
import { getValidFilterTypes } from '~/screens/MyAgency/components/utils';
import { GroupedFilters } from '~/screens/MyAgency/hooks/useGroupFilters';
import { AdvancedFilters } from '~/screens/MyAgency/store';
import { convertPxToRems } from '~/utils-ts';

const { format } = Intl.NumberFormat('en', { notation: 'compact' });

const PerformanceMetricsMap = {
  followers: 'Followers',
  reachEngagementRate: 'Avg. Reach Eng. Rate',
  averageFeedReach: 'Avg. Feed Reach',
  averageStoryReach: 'Avg. Story Reach',
  averageReelsViews: 'Avg. Reels View',
  subscribers: 'Subscribers',
  averageViews: 'Avg. Views',
  averageEngagementRate: 'Avg. Eng. Rate',
  avgFeedReachEngRate: 'Reach Eng. Rate',
  avgFeedReach: 'Avg. Feed Reach',
  avgStoryReach: 'Avg. Story Reach',
  avgReelsPlays: 'Avg. Reels Views',
  avgViews: 'Avg. Views',
  avgEngRate: 'Avg. Eng. Rate',
};

interface Props {
  filter: GroupedFilters;
  clearFilters: AdvancedFilters['clearFilters'];
}

export const AdvancedFilterPill: React.FC<Props> = ({
  filter,
  clearFilters,
}) => {
  const filterCategory =
    filter.filterType === 'age' ||
    filter.filterType === 'gender' ||
    filter.filterType === 'country'
      ? 'demographics'
      : 'performance';
  return (
    <Chip
      rightIcon={<CloseIconNew />}
      onRightIconClick={() => {
        clearFilters(filter.platform, getValidFilterTypes(filter));
      }}
    >
      <Flex alignItems="center" color="primary.400" gap={convertPxToRems(6)}>
        {/* NOTE: Reach icon needs to be updated when we implement performance metrics */}
        {filterCategory === 'demographics' ? (
          <>
            <ReachIcon fill="primary.400" width={convertPxToRems(16)} />
            <Text flex="1">
              {filter.platformSlug}: {getFilterName(filter)}
              {filter.criteria ? ` (≥${filter.criteria})` : null}
            </Text>
          </>
        ) : (
          <>
            <FireIcon fill="primary.400" width={convertPxToRems(16)} />
            <Text flex="1">
              {filter.platformSlug}: {/* @ts-ignore */}
              {PerformanceMetricsMap[filter.filterType]} (≥
              {!isEngagementRate(filter.platform, filter.filterType)
                ? format(filter.value as number)
                : Math.trunc(parseFloat(format(filter.value as number)) * 100)}
              )
            </Text>
          </>
        )}
      </Flex>
    </Chip>
  );
};

export const AdvancedFilterPillV2: React.FC<Props> = ({
  filter,
  clearFilters,
}) => {
  const filterCategory =
    filter.filterType === 'age' ||
    filter.filterType === 'gender' ||
    filter.filterType === 'country'
      ? 'demographics'
      : 'performance';

  return (
    <Tag
      variant="secondary"
      onClick={() => {
        clearFilters(filter.platform, getValidFilterTypes(filter));
      }}
    >
      {filterCategory === 'demographics' ? (
        <div className="flex items-center gap-1.5">
          <Megaphone size={16} className="text-neutral-400" />
          <span className="flex-1">
            {filter.platformSlug}: {getFilterName(filter)}
            {filter.criteria ? ` (≥${filter.criteria})` : null}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <Flame size={16} className="text-neutral-400" />
          <span className="flex-1">
            {filter.platformSlug}: {/* @ts-ignore */}
            {PerformanceMetricsMap[filter.filterType]} (≥
            {!isEngagementRate(filter.platform, filter.filterType)
              ? format(filter.value as number)
              : Math.trunc(parseFloat(format(filter.value as number)) * 100)}
            )
          </span>
        </div>
      )}
    </Tag>
  );
};
